import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DndProvider, useDrag, useDragLayer, useDrop } from 'react-dnd';
import { MultiBackend } from 'dnd-multi-backend';
import { HTML5toTouch } from 'rdndmb-html5-to-touch';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import ConfigurableTable from 'components/shared/table/ConfigurableTable';
import {
  BOOLEAN_TRUE,
  EMPTY_STRING,
  GENERAL_INSIGHT_BOARDS_COLUMNS,
  NULL_VALUE,
  NUMBER_ONE,
  NUMBER_TWO,
  NUMBER_ZERO,
  PIN_ACTIONS,
} from 'constants/constants';
import classes from './PinnedInsightBoards.module.scss';
import OverlayTrigger from 'components/shared/tooltip/Tooltip';

const DragItemType = { BOARD: 'board' };

const DraggableBodyRow = ({ index, moveRow, className, ...restProps }) => {
  const ref = useRef(NULL_VALUE);
  const [dropIndicatorClass, setDropIndicatorClass] = useState(EMPTY_STRING);

  const [{ isOver }, drop] = useDrop({
    accept: DragItemType.BOARD,
    canDrop: () => BOOLEAN_TRUE,
    drop: () => ({ index }),
    collect: (monitor) => ({
      isOver: monitor.isOver({ shallow: BOOLEAN_TRUE }),
    }),
    hover: (draggedItem, monitor) => {
      if (!ref.current) return;

      const dragIndex = draggedItem.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        setDropIndicatorClass(EMPTY_STRING);
        return;
      }

      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / NUMBER_TWO;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex) {
        setDropIndicatorClass('drop-indicator-bottom');
        if (hoverClientY < hoverMiddleY) return;
      }

      if (dragIndex > hoverIndex) {
        setDropIndicatorClass('drop-indicator-top');
        if (hoverClientY > hoverMiddleY) return;
      }

      moveRow(dragIndex, hoverIndex);
      draggedItem.index = hoverIndex;
    },
  });

  useEffect(() => {
    if (!isOver) {
      setDropIndicatorClass(EMPTY_STRING);
    }
  }, [isOver]);

  drop(ref);

  return (
    <tr
      ref={ref}
      className={`${className || EMPTY_STRING} ${dropIndicatorClass} ${isOver ? 'drop-target-row' : EMPTY_STRING}`}
      {...restProps}
    />
  );
};

const GripCell = ({
  dragHandleRef,
  index,
  rowData,
  isDraggable,
  onReorder,
  originalBoards,
  restoreBoards,
}) => {
  const [{ isDragging }, drag, preview] = useDrag(
    {
      type: DragItemType.BOARD,
      canDrag: () => isDraggable,
      item: () => {
        const rowElement = dragHandleRef?.current?.closest('tr');
        const tableEl = dragHandleRef?.current?.closest('table');
        const scrollContainer = tableEl?.parentElement;
        const rowWidth = rowElement?.getBoundingClientRect()?.width || 1112;
        const visibleWidth = scrollContainer?.clientWidth || rowWidth;
        const containerLeft =
          scrollContainer?.getBoundingClientRect().left ?? NUMBER_ZERO;
        const columnWidths = rowElement
          ? Array.from(rowElement.querySelectorAll('td')).map(
              (cell) => cell.getBoundingClientRect().width
            )
          : undefined;
        return {
          index,
          originalIndex: index,
          originalBoards,
          board: rowData,
          previewWidth: visibleWidth,
          containerLeft,
          columnWidths,
        };
      },
      end: async (item, monitor) => {
        if (!monitor.didDrop()) {
          restoreBoards?.(item?.originalBoards);
          return;
        }

        const finalIndex = item?.index;
        const originalIndex = item?.originalIndex;

        if (
          typeof finalIndex !== 'number' ||
          finalIndex === originalIndex ||
          !item?.board?.Id
        ) {
          restoreBoards?.(item?.originalBoards);
          return;
        }

        const reorderSucceeded = await onReorder?.({
          custInsightBrdId: item?.board?.Id,
          action: PIN_ACTIONS.REORDER,
          positionIndex: finalIndex + NUMBER_ONE,
        });

        if (!reorderSucceeded) {
          restoreBoards?.(item?.originalBoards);
        }
      },
    },
    [index, rowData, isDraggable, onReorder, originalBoards, restoreBoards]
  );

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: BOOLEAN_TRUE });
  }, [preview]);

  useEffect(() => {
    const rowElement = dragHandleRef?.current?.closest('tr');
    if (!rowElement) return;

    if (isDragging) {
      rowElement.classList.add('drag-source-row');
    } else {
      rowElement.classList.remove('drag-source-row');
    }

    return () => rowElement.classList.remove('drag-source-row');
  }, [isDragging, dragHandleRef]);

  drag(dragHandleRef);

  return (
    <div
      style={{ cursor: isDraggable ? 'grab' : 'default' }}
      className='me-2'
      ref={dragHandleRef}>
      <i className='icon icon__grip-vertical'></i>
    </div>
  );
};

const CustomDragLayer = () => {
  const DRAG_PREVIEW_Y_OFFSET = 25;
  const { item, initialOffset, currentOffset, clientOffset, isDragging } =
    useDragLayer((monitor) => ({
      item: monitor.getItem(),
      initialOffset: monitor.getInitialSourceClientOffset(),
      currentOffset: monitor.getSourceClientOffset(),
      clientOffset: monitor.getClientOffset(),
      isDragging: monitor.isDragging(),
    }));

  const activeElement = clientOffset
    ? document.elementFromPoint(clientOffset.x, clientOffset.y)
    : NULL_VALUE;
  const hoveredRow = activeElement?.closest('tr');
  const isValidDropTarget = Boolean(
    hoveredRow?.closest('tbody')?.classList?.contains('ant-table-tbody')
  );

  if (!isDragging) return NULL_VALUE;

  const getItemStyles = (initialOffset, currentOffset) => {
    if (!initialOffset || !currentOffset) return { display: 'none' };
    const deltaX = currentOffset.x - initialOffset.x;
    const x = (item?.containerLeft ?? initialOffset.x) + deltaX;
    const { y } = currentOffset;
    const transform = `translate(${x}px, ${y - DRAG_PREVIEW_Y_OFFSET}px)`;
    return { transform, WebkitTransform: transform };
  };

  const previewWidth = item?.previewWidth || 1112;
  const getPreviewCellWidth = (index, fallback) =>
    item?.columnWidths?.[index] || fallback;

  return (
    <div
      className={classes['custom-drag-layer']}
      style={{ cursor: isValidDropTarget ? 'grabbing' : 'not-allowed' }}>
      <div style={getItemStyles(initialOffset, currentOffset)}>
        <div
          className={classes.dragging}
          style={{ width: previewWidth, minWidth: previewWidth }}>
          <div
            className={classes.previewCell}
            style={{
              width: getPreviewCellWidth(0, 472),
            }}>
            <i className='icon icon__grip-vertical flex-shrink-0 me-3'></i>
            <i className='icon icon__pin_active flex-shrink-0 me-3' />
            <span className='span-link text text__normal text__bold text-wrap text-break'>
              {item?.board?.name}
            </span>
          </div>
          <div
            className={classes.previewCell}
            style={{
              width: getPreviewCellWidth(1, 180),
            }}>
            <span className='text text__black'>
              {item?.board?.createdDate
                ? dayjs(item.board.createdDate).format('MMM DD, YYYY')
                : '-'}
            </span>
          </div>
          <div
            className={classes.previewCell}
            style={{
              width: getPreviewCellWidth(2, 180),
            }}>
            <span className='text text__black'>
              {item?.board?.lastViewedDate
                ? dayjs(item.board.lastViewedDate).format('MMM DD, YYYY')
                : '-'}
            </span>
          </div>
          <div
            className={classes.previewCell}
            style={{
              width: getPreviewCellWidth(3, 180),
            }}>
            <span className='text text__black'>{item?.board?.boardType}</span>
          </div>
          <div
            className={classes.previewCell}
            style={{
              width: getPreviewCellWidth(4, 100),
            }}>
            <i className='m-0 icon icon__card-toggle' />
          </div>
        </div>
      </div>
    </div>
  );
};

const PinnedInsightBoards = ({
  pinnedBoards,
  onItemClickHandler,
  onActionClick,
  onUnpin,
  onReorder,
  sharedByTooltip,
  isPinActionInProgress,
  searchedText,
}) => {
  const getBoardKey = (item, index) => item.Id || index;
  const sourceOrderSignature = (pinnedBoards || [])
    .map((item, index) => {
      const key = getBoardKey(item, index);
      return `${key}~${item?.name}`;
    })
    .join('|');

  const [localBoards, setLocalBoards] = useState(pinnedBoards || []);
  const tableRef = useRef(NULL_VALUE);
  const dragHandleRefs = useRef({});
  const lastSyncedSignatureRef = useRef(sourceOrderSignature);

  useEffect(() => {
    setLocalBoards(pinnedBoards || []);
    lastSyncedSignatureRef.current = sourceOrderSignature;
  }, [pinnedBoards, sourceOrderSignature]);

  const moveRow = (fromIndex, toIndex) => {
    setLocalBoards((prev) => {
      const updated = [...prev];
      const [moved] = updated.splice(fromIndex, NUMBER_ONE);
      updated.splice(toIndex, NUMBER_ZERO, moved);
      return updated;
    });
  };

  const restoreBoards = (boards) => {
    if (!boards) return;
    setLocalBoards(boards);
  };

  useEffect(() => {
    const container = tableRef.current?.querySelector('.ant-table-body');
    if (!container) return;

    const edge = 60,
      speed = 10;

    const onTouchMove = ({ touches }) => {
      const { top, bottom } = container.getBoundingClientRect();
      const y = touches[0]?.clientY;
      if (y > bottom - edge) container.scrollTop += speed;
      else if (y < top + edge) container.scrollTop -= speed;
    };

    document.addEventListener('touchmove', onTouchMove);
    return () => document.removeEventListener('touchmove', onTouchMove);
  }, [localBoards.length]);

  const basePinnedColumns = useMemo(
    () =>
      GENERAL_INSIGHT_BOARDS_COLUMNS.map((column) => ({
        ...column,
        sorter: undefined,
        defaultSortOrder: undefined,
        width: column.key === 'boardType' ? 180 : column.width,
      })),
    []
  );

  if (!localBoards?.length) {
    return (
      <div
        ref={tableRef}
        className={classes.wrapper}>
        <p className='d-flex align-items-center align-content-center mb-3'>
          <i className='icon icon__pin_title me-2'></i>
          <span className='title__base title__bold ms-1'>Pinned Boards</span>
        </p>
        <div className={classes.emptyStateContainer}>
          <p className='d-flex text text__small text__black align-items-center mb-0'>
            {searchedText ? (
              'No search results to display.'
            ) : (
              <>
                Pin your frequently used Insight Boards by clicking the
                <i className='icon icon__pin_disabled icon__pin_disabled_lg mx-2'></i>
                icon below.
              </>
            )}
          </p>
        </div>
      </div>
    );
  }

  const getColumns = () => {
    const dragHandleColumn = {
      ...basePinnedColumns[0],
      key: 'dragHandle',
      title: EMPTY_STRING,
      render: (_, record) => {
        const refKey = record.key;
        if (!dragHandleRefs.current[refKey]) {
          dragHandleRefs.current[refKey] = React.createRef();
        }
        return (
          <div className='d-flex align-items-center'>
            <GripCell
              dragHandleRef={dragHandleRefs.current[refKey]}
              index={record.dragIndex}
              rowData={record}
              isDraggable={
                localBoards.length > NUMBER_ONE && !isPinActionInProgress
              }
              onReorder={onReorder}
              originalBoards={localBoards}
              restoreBoards={restoreBoards}
            />
            <i
              className='icon icon__pin_active me-3 flex-shrink-0'
              onClick={(e) => {
                if (isPinActionInProgress) return;
                e.stopPropagation();
                onUnpin?.(record);
              }}
              title='Unpin board'
            />
            <span className='span-link text text__normal text__bold flex-grow-1 text-wrap text-break'>
              <Link
                to={record.name}
                onClick={onItemClickHandler?.bind(NULL_VALUE, record)}>
                {record.name}
              </Link>
            </span>
            {record.sharedBy ? (
              <span className='ms-2 flex-shrink-0'>
                <OverlayTrigger overlay={sharedByTooltip(record.sharedBy)}>
                  <i className='icon icon__sharedby'></i>
                </OverlayTrigger>
              </span>
            ) : (
              NULL_VALUE
            )}
          </div>
        );
      },
    };

    return [dragHandleColumn, ...basePinnedColumns.slice(1)];
  };

  const tableRows = localBoards.map((item, index) => ({
    ...item,
    Id: item.Id,
    key: getBoardKey(item, index),
    dragIndex: index,
  }));

  return (
    <DndProvider
      backend={MultiBackend}
      options={HTML5toTouch}>
      <div
        ref={tableRef}
        className={classes.wrapper}>
        <p className='d-flex align-items-center mb-3'>
          <i className='icon icon__pin_title'></i>
          <span className='title__base title__bold'>Pinned Boards</span>
        </p>
        <ConfigurableTable
          tableName={'myViews'}
          rows={tableRows}
          columns={getColumns()}
          showActionIcon={true}
          actionColumnWidth={100}
          iconProps={{ iconCss: 'card-toggle' }}
          onActionClickHandler={(record, e) => onActionClick?.(record, e)}
          pagination={false}
          dimentions={{ x: 1112 }}
          showHeader={false}
          customCss='insights-board'
          tableComponents={{
            body: {
              row: DraggableBodyRow,
            },
          }}
          onRow={(record) => ({
            index: record.dragIndex,
            moveRow,
          })}
        />
      </div>
      <CustomDragLayer />
    </DndProvider>
  );
};

export default PinnedInsightBoards;
